import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plane, PlaneLanding, PlaneTakeoff, Bike, Car, Users, Briefcase, Minus, Plus, MapPinned, Loader2 } from "lucide-react";
import { api, errMsg, fmt } from "../lib/api";
import MapView from "../map/MapView";
import PlaceSearch from "../map/PlaceSearch";
import { getRoute } from "../map/routing";
import { btnGhost, btnPrimary, inputCls } from "../components/ui";

const ICON = { moto: Bike, carro: Car };
const toLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

function Stepper({ icon: Icon, label, value, set, min, max, testid }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-gray-100 p-2" data-testid={testid}>
      <Icon className="h-4 w-4 text-bo-green" /><span className="flex-1 text-sm">{label}</span>
      <button type="button" onClick={() => set(Math.max(min, value - 1))} className="grid h-8 w-8 place-items-center rounded-full border" data-testid={`${testid}-minus`}><Minus className="h-3.5 w-3.5" /></button>
      <strong className="w-5 text-center" data-testid={`${testid}-value`}>{value}</strong>
      <button type="button" onClick={() => set(Math.min(max, value + 1))} className="grid h-8 w-8 place-items-center rounded-full border" data-testid={`${testid}-plus`}><Plus className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function VehicleCard({ o, selected, onSelect }) {
  const Icon = ICON[o.vehicle] || Car;
  const disabled = !o.fits;
  return (
    <button type="button" disabled={disabled} onClick={() => onSelect(o.vehicle)} data-testid={`transfer-vehicle-${o.vehicle}`}
      className={`flex items-center gap-3 rounded-2xl border-2 p-3 text-left transition-colors ${selected ? "border-bo-green bg-bo-green/5" : "border-gray-100"} ${disabled ? "opacity-50" : "hover:border-gray-300"}`}>
      <Icon className="h-8 w-8 shrink-0 text-bo-green" />
      <span className="flex-1 text-sm">
        <strong className="block">{o.label}</strong>
        <span className="text-xs text-gray-500">{o.passengers} pass. · {o.luggage} malas · {o.note}</span>
        <span className="block text-xs text-gray-600">{o.estimate.distance_km} km · ~{o.estimate.duration_min} min</span>
        {!o.fits && <span className="block text-xs font-semibold text-red-600" data-testid={`transfer-vehicle-${o.vehicle}-reason`}>{o.reason}</span>}
        {o.fits && !o.available && <span className="block text-xs font-semibold text-red-600" data-testid={`transfer-vehicle-${o.vehicle}-unavailable`}>Sem motorista DEMO disponível agora (pode agendar)</span>}
      </span>
      <span className="text-right"><strong className="block" data-testid={`transfer-vehicle-${o.vehicle}-price`}>{fmt(o.estimate.price)}</strong>
        {o.available && o.fits && <span className="text-[10px] font-bold uppercase text-amber-700">DEMO disponível</span>}</span>
    </button>
  );
}

function WhenFields({ dir, when, setWhen, pickupAt, setPickupAt, landingAt, setLandingAt, flight, setFlight }) {
  const min = toLocal(new Date(Date.now() + 31 * 60000));
  return (
    <div className="flex flex-col gap-2" data-testid="transfer-when">
      <div className="grid grid-cols-2 gap-2">
        {[["now", "Agora"], ["scheduled", "Agendar"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setWhen(v)} className={`${when === v ? btnPrimary : btnGhost} py-2`} data-testid={`transfer-when-${v}`}>{l}</button>
        ))}
      </div>
      {dir === "arrival" && when === "scheduled" && (
        <label className="text-xs text-gray-600">Hora prevista de aterragem (opcional)
          <input type="datetime-local" value={landingAt} onChange={(e) => setLandingAt(e.target.value)} className={inputCls} data-testid="transfer-landing-input" /></label>
      )}
      {when === "scheduled" && (
        <label className="text-xs text-gray-600">{dir === "arrival" ? "Hora pretendida de recolha no aeroporto" : "Hora de recolha"}
          <input type="datetime-local" min={min} value={pickupAt} onChange={(e) => setPickupAt(e.target.value)} className={inputCls} data-testid="transfer-pickup-input" /></label>
      )}
      {dir === "arrival" && (
        <label className="text-xs text-gray-600">Número do voo (opcional)
          <input value={flight} onChange={(e) => setFlight(e.target.value.toUpperCase())} maxLength={10} placeholder="ex.: TP1525" className={inputCls} data-testid="transfer-flight-input" />
          <span className="text-[11px] text-gray-500">Apenas informativo para o motorista. Não existe acompanhamento automático do voo.</span></label>
      )}
    </div>
  );
}

export default function AirportTransfer({ onCreated, defaultWhen = "now" }) {
  const [cfg, setCfg] = useState(null);
  const [dir, setDir] = useState("arrival");
  const [place, setPlace] = useState(null);
  const [pickOnMap, setPickOnMap] = useState(false);
  const [pax, setPax] = useState(1);
  const [bags, setBags] = useState(0);
  const [route, setRoute] = useState(null);
  const [opts, setOpts] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [when, setWhen] = useState(defaultWhen);
  const [pickupAt, setPickupAt] = useState("");
  const [landingAt, setLandingAt] = useState("");
  const [flight, setFlight] = useState("");
  const [busy, setBusy] = useState(false);
  const [pickSeq, setPickSeq] = useState(0);
  useEffect(() => { api.get("/ride-demo/transfer/options").then(({ data }) => setCfg(data)); }, []);
  const airport = cfg?.airport;
  const [from, to] = dir === "arrival" ? [airport, place] : [place, airport];
  useEffect(() => {
    setRoute(null); setOpts(null);
    if (!from || !to) return;
    getRoute(from, to).then((r) => {
      // A road route ending far from the chosen point means it was placed off-road (e.g. at sea).
      const end = dir === "arrival" ? r.coords.at(-1) : r.coords[0];
      if (r.road && Math.hypot(end[0] - place.lat, (end[1] - place.lng) * Math.cos(place.lat * Math.PI / 180)) * 111 > 0.6) {
        toast.error("Esse ponto não tem acesso por estrada. Escolha um local em terra, perto de uma estrada.");
        setPlace(null);
        return;
      }
      setRoute(r);
    });
  }, [dir, place, airport]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!route || !place) return;
    setOpts(null);
    api.post("/ride-demo/transfer/quote", { direction: dir, place, passengers: pax, luggage: bags, route_km: route.road ? route.distanceKm : null, route_min: route.road ? route.durationMin : null })
      .then(({ data }) => { setOpts(data.options); setVehicle((v) => (data.options.find((o) => o.vehicle === v && o.fits) ? v : (data.options.find((o) => o.fits && o.available) || data.options.find((o) => o.fits))?.vehicle || null)); })
      .catch((e) => toast.error(errMsg(e)));
  }, [route, pax, bags]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cfg) return null;

  const choose = (p) => { setPickSeq((n) => n + 1); setPlace({ lat: p.lat, lng: p.lng, label: (p.label || "Local escolhido no mapa").slice(0, 120) }); setPickOnMap(false); };
  const confirm = async () => {
    setBusy(true);
    try {
      const body = { direction: dir, place, passengers: pax, luggage: bags, vehicle, when, flight_number: flight,
        route_km: route?.road ? route.distanceKm : null, route_min: route?.road ? route.durationMin : null,
        pickup_at: when === "scheduled" && pickupAt ? new Date(pickupAt).toISOString() : null,
        landing_at: dir === "arrival" && landingAt ? new Date(landingAt).toISOString() : null };
      const { data } = await api.post("/ride-demo/transfer/trips", body);
      toast.success(when === "now" ? `Transfer ${data.number} pedido` : `Reserva ${data.number} agendada`);
      onCreated();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const label = dir === "arrival" ? "Destino (hotel, residência...)" : "Local de recolha";
  return (
    <section className="bo-card flex flex-col gap-3 p-3 sm:p-5" data-testid="transfer-form">
      <h2 className="flex items-center gap-2 font-bold"><Plane className="h-5 w-5 text-bo-green" />Transfer Aeroporto</h2>
      <div className="grid grid-cols-2 gap-2">
        {[["arrival", "Chegada", PlaneLanding], ["departure", "Partida", PlaneTakeoff]].map(([v, l, I]) => (
          <button key={v} type="button" onClick={() => setDir(v)} className={`${dir === v ? btnPrimary : btnGhost} flex items-center justify-center gap-2 py-2`} data-testid={`transfer-dir-${v}`}><I className="h-4 w-4" />{l}</button>
        ))}
      </div>
      <p className="rounded-2xl bg-bo-bg p-3 text-sm" data-testid="transfer-airport">{dir === "arrival" ? "Recolha: " : "Destino: "}<strong>{airport.label}</strong></p>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold">{label}</span>
        <PlaceSearch key={`${dir}-${pickSeq}`} onPick={choose} placeholder="Pesquisar hotel, bairro ou localidade" testid="transfer-search" />
        <div className="flex flex-wrap gap-2">
          {cfg.places.filter((p) => p.id !== "airport").map((p) => (
            <button key={p.id} type="button" disabled={!p.active} onClick={() => choose(p)} title={p.source}
              className="rounded-full border border-gray-200 px-3 py-1 text-xs disabled:opacity-40" data-testid={`transfer-place-${p.id}`}>{p.label}{!p.active && " (a confirmar)"}</button>
          ))}
          <button type="button" onClick={() => setPickOnMap(!pickOnMap)} className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs" data-testid="transfer-pick-map-btn"><MapPinned className="h-3.5 w-3.5" />Escolher no mapa</button>
        </div>
        {place && <p className="text-xs text-gray-600" data-testid="transfer-place-value">{label}: <strong>{place.label}</strong></p>}
      </div>
      {(pickOnMap || place) && (
        <MapView height={240} onClick={pickOnMap ? choose : undefined} route={route} testid="transfer-map"
          markers={[{ key: "a", pos: from, icon: "me", label: "Recolha" }, { key: "b", pos: to, icon: "dest", label: "Destino" }]} />
      )}
      {pickOnMap && <p className="text-xs text-gray-500">Toque no mapa para marcar o {dir === "arrival" ? "destino" : "local de recolha"}.</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        <Stepper icon={Users} label="Passageiros" value={pax} set={setPax} min={1} max={8} testid="transfer-pax" />
        <Stepper icon={Briefcase} label="Malas" value={bags} set={setBags} min={0} max={8} testid="transfer-bags" />
      </div>
      {place && !opts && <p className="flex items-center gap-2 text-sm text-gray-500" data-testid="transfer-quote-loading"><Loader2 className="h-4 w-4 animate-spin" />A calcular percurso e tarifas...</p>}
      {opts && (
        <div className="flex flex-col gap-2" data-testid="transfer-vehicles">
          {opts.map((o) => <VehicleCard key={o.vehicle} o={o} selected={vehicle === o.vehicle} onSelect={setVehicle} />)}
          {!opts.some((o) => o.fits) && <p className="text-xs font-semibold text-red-600" data-testid="transfer-no-fit">Nenhuma categoria DEMO comporta este número de passageiros e malas.</p>}
        </div>
      )}
      {opts && vehicle && (
        <>
          <WhenFields {...{ dir, when, setWhen, pickupAt, setPickupAt, landingAt, setLandingAt, flight, setFlight }} />
          {when === "now" && !opts.find((o) => o.vehicle === vehicle)?.available && <p className="text-xs font-semibold text-red-600" data-testid="transfer-now-unavailable">Sem motorista DEMO disponível agora para esta categoria. Escolha outra categoria ou agende.</p>}
          <p className="text-xs text-amber-800">Tarifa DEMO calculada no servidor, em STN · nada é cobrado{when === "scheduled" && " · reserva DEMO sem garantia de disponibilidade real"}</p>
          <button disabled={busy || (when === "scheduled" && !pickupAt) || (when === "now" && !opts.find((o) => o.vehicle === vehicle)?.available)} onClick={confirm} className={`${btnPrimary} w-full disabled:opacity-50`} data-testid="transfer-confirm-btn">
            {busy ? "A confirmar..." : `${when === "now" ? "Pedir" : "Agendar"} transfer DEMO · ${fmt(opts.find((o) => o.vehicle === vehicle)?.estimate.price)}`}
          </button>
        </>
      )}
    </section>
  );
}
